import { useState } from "react";
import { useSelector } from "react-redux";
import EBLConsentHeader from "../EBLConsentHeader";
import { RootState } from "../../template/store";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.min.css";
import { Button, Card } from "react-bootstrap";

type ConsentForm = {
  firstSignatory: string;
  firstJobTitle: string;
  firstDate: Date | null;
  secondSignatory: string;
  secondJobTitle: string;
  secondDate: Date | null;
};

type ConsentErrors = Partial<Record<keyof ConsentForm, string>>;

const EBLConsent = ({ onSubmit }: { onSubmit: (data: ConsentForm) => void }) => {
  const companyName =
    useSelector(
      (state: RootState) => state.userProfile?.profile?.partnerName
    ) ?? "";

	const today = new Date();
	today.setHours(0, 0, 0, 0);

  const [form, setForm] = useState<ConsentForm>({
    firstSignatory: "",
    firstJobTitle: "",
    firstDate: today,
    secondSignatory: "",
    secondJobTitle: "",
    secondDate: today,
  });

  const [errors, setErrors] = useState<ConsentErrors>({});

  const update = (key: keyof ConsentForm, value: any) => {
    setForm({ ...form, [key]: value });
  };

	const validate = (): boolean => {
	  const newErrors: ConsentErrors = {};

	  (Object.keys(form) as (keyof ConsentForm)[]).forEach((key) => {
	    const value = form[key];

	    // String fields
	    if (typeof value === "string") {
	      if (!value.trim()) {
	        newErrors[key] = "required";
	      }
	    }

	    // Date fields
	    if (value instanceof Date === false && (key === "firstDate" || key === "secondDate")) {
	      newErrors[key] = "required";
	    }
	  });

	  setErrors(newErrors);
	  return Object.keys(newErrors).length === 0;
	};


	const handleSubmit = () => {
	  if (!validate()) {
	    toast.error("Required fields are missing");
	    return;
	  }
		onSubmit({
		  ...form,
		  firstDate: form.firstDate?.toISOString() as any,
		  secondDate: form.secondDate?.toISOString() as any,
		});

	  toast.success("Consent submitted successfully");

    // Optional: reset form after success
    setForm({
      firstSignatory: "",
      firstJobTitle: "",
      firstDate: today,
      secondSignatory: "",
      secondJobTitle: "",
      secondDate: today,
    });

    setErrors({});
	};

	const inputClass = (field: keyof ConsentForm) =>
	  `form-control border-0 border-bottom ${
	    errors[field] ? "border-danger" : "border-secondary"
	  } rounded-0 shadow-none display-inline`;

  return (
    <Card className='h-100 overflow-auto'>
      <EBLConsentHeader />

      <Card.Body>
	      <div className="ebl-consent-wrapper font-sans-serif">
	        <div className="ebl-consent-content">
	          <div className="p-4 max-w-4xl mx-auto text-sm leading-6">
	            <p className="mb-4">
	              Enrolling Customer details <strong>{companyName}</strong>
	            </p>

	            <p className="text-justify">
	              This deed serves as notice of the following to IQAX, GSBN and <strong>{companyName}</strong>
	            </p>

	            <ol className="list-decimal ml-6">
	              <li className="mt-4">
	                <p>
	                  <strong>Authority to Process eBLs</strong>
	                </p>
	                <p className="text-justify">
	                  <strong>{companyName}</strong> hereby authorizes ODeX to process and handle
	                  electronic Bills of Lading (eBLs) issued by a carrier via IQAX
	                  eBL in connection with shipments. <strong>{companyName}</strong> grants the
	                  authority to facilitate the management of such eBLs through its
	                  platform or associated systems.
	                </p>
	              </li>

	              <li className="mt-4">
	                <p>
	                  <strong>Acceptance of IQAX Terms and Conditions</strong>
	                </p>
	                <p className="text-justify">
	                  <strong>{companyName}</strong> further confirms that it agrees and accepts that
	                  any such eBLs are subject to the IQAX Website TOU (
	                  <a
	                    href="https://www.iqax.com/tou.htm"
	                    target="_blank"
	                    rel="noreferrer"
	                    className="text-blue-600 underline"
	                  >
	                    https://www.iqax.com/tou.htm
	                  </a>
	                  ), including the IQAX eBL T&amp;C (
	                  <a
	                    href="https://www.iqax.com/ebl_tc.pdf"
	                    target="_blank"
	                    rel="noreferrer"
	                    className="text-blue-600 underline"
	                  >
	                    https://www.iqax.com/ebl_tc.pdf
	                  </a>
	                  ), as may be updated from time to time. <strong>{companyName}</strong> acknowledges that
										these terms govern the rights, responsibilities, and liabilities
	                  associated with the use of IQAX’s eBL platform.
	                </p>
	              </li>

	              <li className="mt-4">
	                <p>
	                  <strong>Applicable Law and Jurisdiction</strong>
	                </p>
	                <p className="text-justify">
	                  This Deed is subject to English law and the non-exclusive
	                  jurisdiction of the English courts.
	                </p>
	              </li>
	            </ol>

					    <p className="text-justify">
					      This deed shall remain valid unless revoked by <strong>{companyName}</strong> in writing.
					      The revocation shall take effect 10 business days after notification
					      or such later date as specified in the notice. Notification of revocation
					      shall be by email sent to IQAX -{" "}
					      <a href="mailto:customerservices@iqax.com">
					        customerservices@iqax.com
				        </a>, with copies
					      sent to GSBN -{" "}
	              <a href="mailto:customer.support@gsbn.trade">
	                customer.support@gsbn.trade
	              </a>
	               {" "}and{" "}
	              <a href="mailto:notification@odexservices.com">
	                notification@odexservices.com
	              </a>.
					    </p>

	            <hr className="my-4" />

	            <p>
	              <strong>Declaration by the Customer’s signatories to this document</strong>
	            </p>

	            <p className="text-justify">
	              By signing this document, each authorised signatory, director or company
	              secretary of Customer represents and warrants that they have read this document,
	              are a duly authorised representative of Customer with full power and authority to
	              execute this document and bind Customer to the terms of this document.
	            </p>

	            <p>
	              Executed as a deed.
	            </p>

	            <p>
	                Signed, sealed and delivered by <strong>{companyName}</strong>
	            </p>

	            <table className="w-full mt-2">
	              <thead>
	                <tr>
	                  <th colSpan={2} className="p-2 text-left font-semibold">
	                    First Authorised Signatory / Director
	                  </th>
	                  <th colSpan={2} className="p-2 text-left font-semibold">
	                    Second Authorised Signatory / Director
	                  </th>
	                </tr>
	              </thead>

	              <tbody>
	                {/* Signature */}
	                <tr>
	                  <td className="p-2"><span style={{ color: "red"}}>*</span>Signature:</td>
	                  <td className="p-2">
	                    <input
	                      className={inputClass("firstSignatory")}
	                      value={form.firstSignatory}
	                      placeholder="FULL NAME OF DIRECTOR"
	                      onChange={(e) =>
	                        update("firstSignatory", e.target.value)
	                      }
	                    />
	                  </td>

	                  <td className="p-2"><span style={{ color: "red"}}>*</span>Signature:</td>
	                  <td className="p-2">
	                    <input
	                      className={inputClass("secondSignatory")}
	                      value={form.secondSignatory}
	                      placeholder="FULL NAME OF DIRECTOR"
	                      onChange={(e) =>
	                        update("secondSignatory", e.target.value)
	                      }
	                    />
	                  </td>
	                </tr>

	                {/* Job Title */}
	                <tr>
	                  <td className="p-2"><span style={{ color: "red"}}>*</span>Job Title:</td>
	                  <td className="p-2">
	                    <input
	                      className={inputClass("firstJobTitle")}
	                      value={form.firstJobTitle}
	                      onChange={(e) =>
	                        update("firstJobTitle", e.target.value)
	                      }
	                    />
	                  </td>

	                  <td className="p-2"><span style={{ color: "red"}}>*</span>Job Title:</td>
	                  <td className="p-2">
	                    <input
	                      className={inputClass("secondJobTitle")}
	                      value={form.secondJobTitle}
	                      onChange={(e) =>
	                        update("secondJobTitle", e.target.value)
	                      }
	                    />
	                  </td>
	                </tr>

	                {/* Date */}
	                <tr>
	                  <td className="p-2"><span style={{ color: "red"}}>*</span>Date:</td>
										<td className="p-2">
										  <DatePicker
										    selected={form.firstDate}
										    onChange={(date) => update("firstDate", date as Date)}
										    className={inputClass("firstDate")}
										    wrapperClassName="w-100"
										    placeholderText="Select date"
										    dateFormat="dd/MM/yyyy"
											  minDate={today}
                        maxDate={today}
										    isClearable
										  />
										</td>

	                  <td className="p-2"><span style={{ color: "red"}}>*</span>Date:</td>
										<td className="p-2">
										  <DatePicker
										    selected={form.secondDate}
										    onChange={(date) => update("secondDate", date as Date)}
										    className={inputClass("secondDate")}
										    wrapperClassName="w-100"
										    placeholderText="Select date"
										    dateFormat="dd/MM/yyyy"
											  minDate={today}
                        maxDate={today}
										    isClearable
										  />
										</td>
	                </tr>
	              </tbody>
	            </table>

	            <div className='d-flex justify-content-center align-items-center mt-4'>
	              <Button
	                className='me-2'
	                variant='falcon-primary'
	                size='sm'
	                onClick={handleSubmit}
	                type='submit'
	              >
	                Accept & Submit Consent
	              </Button>
	            </div>
	          </div>
	        </div>
	      </div>
      </Card.Body>
    </Card>
  );
};

export default EBLConsent;
